// src/components/delivery/DeliveryDashboard.jsx
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { fetchDashboardStats, fetchAssignedOrders, updateOrderStatus, verifyOtpAndDeliver } from "../../features/deliverySlice";
import DeliveryStats from "./DeliveryStats";
import DeliveryOrderDetails from "./DeliveryOrderDetails";
import DateFilter from "./DateFilter";
import { RefreshCw, ArrowRight, Package, CheckCircle, Truck } from "react-feather";
import toast from "react-hot-toast";
import config from "../../config";

const DeliveryDashboard = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { stats, orders, deliveryPartner } = useSelector((state) => state.delivery);
  const [refreshing, setRefreshing] = useState(false);
  const [selectedOrder, setSelectedOrder] = useState(null);

  useEffect(() => {
    loadDashboardData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(() => {
      loadDashboardData(true);
    }, 30000);

    return () => clearInterval(interval);
  }, []);

  const loadDashboardData = async (silent = false) => {
    try {
      if (!silent) setRefreshing(true);
      console.log("DeliveryDashboard - Loading dashboard data...");
      
      const results = await Promise.all([
        dispatch(fetchDashboardStats()).unwrap(),
        dispatch(fetchAssignedOrders({ page: 0, size: 20 })).unwrap(), // Fetch more to have enough for both sections
      ]);
      
      console.log("DeliveryDashboard - Stats result:", results[0]);
      console.log("DeliveryDashboard - Orders result:", results[1]);
      
      if (!silent) toast.success("Dashboard refreshed");
    } catch (error) {
      console.error("DeliveryDashboard - Failed to load dashboard data:", error);
      if (!silent) toast.error("Failed to load dashboard data");
    } finally {
      setRefreshing(false);
    }
  };

  // Separate orders into delivery orders and return requests
  const deliveryOrders = orders.content.filter(order => 
    order.deliveryStatus !== 'RETURN_REQUESTED' && 
    order.deliveryStatus !== 'PICKUP_SCHEDULED' && 
    order.deliveryStatus !== 'RETURN_IN_TRANSIT' &&
    order.deliveryStatus !== 'RETURNED' &&
    order.deliveryStatus !== 'BOOK_WITH_ME' &&
    order.deliveryStatus !== 'CANCELLED'
  );

  const returnRequests = orders.content.filter(order => 
    order.deliveryStatus === 'RETURN_REQUESTED' || 
    order.deliveryStatus === 'PICKUP_SCHEDULED' || 
    order.deliveryStatus === 'RETURN_IN_TRANSIT'
  );

  console.log("DeliveryDashboard - Total orders:", orders.content.length);
  console.log("DeliveryDashboard - Delivery orders:", deliveryOrders.length);
  console.log("DeliveryDashboard - Return requests:", returnRequests.length);
  console.log("DeliveryDashboard - All orders:", orders.content.map(o => ({ id: o.orderId, status: o.deliveryStatus })));

  const handleStatClick = (status) => {
    navigate(`/delivery/orders?status=${status}`);
  };

  const handleRefresh = () => {
    loadDashboardData();
  };

  const handleOrderClick = (order) => {
    setSelectedOrder(order);
  };

  const handleCloseDetails = () => {
    setSelectedOrder(null);
  };

  const handleUpdateStatus = async (orderId, newStatus) => {
    try {
      await dispatch(updateOrderStatus({ orderId, status: newStatus })).unwrap();
      toast.success("Order status updated successfully");
      loadDashboardData(true);
    } catch (error) {
      toast.error("Failed to update order status");
    }
  };

  const handleVerifyOtp = async (orderId, otp) => {
    try {
      await dispatch(verifyOtpAndDeliver({ orderId, otp })).unwrap();
      toast.success("Delivery verified successfully! 🎉");
      loadDashboardData(true);
    } catch (error) {
      throw error; // Re-throw to let the modal handle the error display
    }
  };

  return (
    <div className="space-y-6">
      {/* Welcome Section */}
      <div className="bg-gradient-to-r from-primary via-orange-500 to-orange-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-white opacity-5 rounded-full -mr-32 -mt-32"></div>
        <div className="absolute bottom-0 left-0 w-48 h-48 bg-white opacity-5 rounded-full -ml-24 -mb-24"></div>
        <div className="relative flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-1">
              Welcome back, {deliveryPartner?.name || "Delivery Partner"}! 👋
            </h1>
            <p className="text-orange-100 mt-2 text-sm sm:text-base">
              Here's your delivery overview for today
            </p>
          </div>
          <button
            onClick={handleRefresh}
            disabled={refreshing}
            className="flex items-center justify-center space-x-2 px-6 py-3 bg-white text-primary rounded-xl hover:bg-orange-50 transition disabled:opacity-50 font-semibold shadow-lg hover:shadow-xl"
          >
            <RefreshCw
              size={18}
              className={refreshing ? "animate-spin" : ""}
            />
            <span>Refresh</span>
          </button>
        </div>
      </div>

      {/* Statistics Cards */}
      <DeliveryStats
        stats={stats}
        loading={stats.loading}
        onStatClick={handleStatClick}
      />

       {/* Featured Orders - Most Recent Delivery & Return */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Most Recent Delivery Order */}
        <div className="bg-gradient-to-br from-blue-50 via-white to-indigo-50 rounded-2xl shadow-lg border border-blue-100 overflow-hidden transform hover:scale-[1.02] transition-all duration-300">
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Truck size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Next Delivery</h3>
                  <p className="text-blue-100 text-xs">Most recent active order</p>
                </div>
              </div>
              <button
                onClick={() => navigate("/delivery/orders")}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
          
          {orders.loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
            </div>
          ) : deliveryOrders.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package size={32} className="text-blue-400" />
              </div>
              <p className="text-gray-600 font-medium">No active deliveries</p>
              <p className="text-sm text-gray-500 mt-1">New orders will appear here</p>
            </div>
          ) : (
            <div 
              className="p-6 cursor-pointer hover:bg-blue-50/50 transition"
              onClick={() => handleOrderClick(deliveryOrders[0])}
            >
              <div className="flex gap-4">
                {deliveryOrders[0].bookCoverImage && (
                  <img
                    src={
                      deliveryOrders[0].bookCoverImage && deliveryOrders[0].bookCoverImage !== "defaultImage.jpg"
                        ? `${config.url}${deliveryOrders[0].bookCoverImage}`
                        : "/images/defaultBook.png"
                    }
                    alt={deliveryOrders[0].bookName}
                    className="w-20 h-28 object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.src = "/images/defaultBook.png";
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-base line-clamp-1">
                        {deliveryOrders[0].bookName}
                      </h4>
                      <p className="text-sm text-gray-600">{deliveryOrders[0].bookAuthor}</p>
                    </div>
                    <span
                      className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        deliveryOrders[0].deliveryStatus === "PROCESSING"
                          ? "bg-blue-100 text-blue-800"
                          : deliveryOrders[0].deliveryStatus === "SHIPPED"
                          ? "bg-purple-100 text-purple-800"
                          : "bg-yellow-100 text-yellow-800"
                      }`}
                    >
                      {deliveryOrders[0].deliveryStatus.replace(/_/g, " ")}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mt-3">
                    <div className="flex items-center text-sm text-gray-700">
                      <div className="w-6 h-6 bg-blue-100 rounded-full flex items-center justify-center mr-2">
                        <span className="text-blue-600 font-semibold text-xs">
                          {deliveryOrders[0].customerName?.charAt(0) || "?"}
                        </span>
                      </div>
                      <span className="font-medium">{deliveryOrders[0].customerName || "N/A"}</span>
                    </div>
                    
                    <div className="flex items-start text-sm text-gray-600">
                      <Package size={14} className="mr-2 mt-0.5 text-blue-500 flex-shrink-0" />
                      <span className="line-clamp-1">{deliveryOrders[0].deliveryAddress}</span>
                    </div>
                    
                    <div className="flex items-center text-sm font-medium text-blue-600">
                      <span className="mr-1">{deliveryOrders[0].city}</span>
                      <span>•</span>
                      <span className="ml-1">{deliveryOrders[0].pincode}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Most Recent Return Request */}
        <div className="bg-gradient-to-br from-purple-50 via-white to-pink-50 rounded-2xl shadow-lg border border-purple-100 overflow-hidden transform hover:scale-[1.02] transition-all duration-300">
          <div className="bg-gradient-to-r from-purple-500 to-pink-600 px-6 py-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-3">
                <div className="p-2 bg-white/20 rounded-lg">
                  <Package size={20} className="text-white" />
                </div>
                <div>
                  <h3 className="text-white font-bold text-lg">Next Return</h3>
                  <p className="text-purple-100 text-xs">Most recent return request</p>
                </div>
              </div>
              <button
                onClick={() => navigate("/delivery/returns")}
                className="text-white hover:bg-white/20 p-2 rounded-lg transition"
              >
                <ArrowRight size={20} />
              </button>
            </div>
          </div>
          
          {orders.loading ? (
            <div className="p-8 text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
            </div>
          ) : returnRequests.length === 0 ? (
            <div className="p-8 text-center">
              <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                <Package size={32} className="text-purple-400" />
              </div>
              <p className="text-gray-600 font-medium">No return requests</p>
              <p className="text-sm text-gray-500 mt-1">Return requests will appear here</p>
            </div>
          ) : (
            <div 
              className="p-6 cursor-pointer hover:bg-purple-50/50 transition"
              onClick={() => handleOrderClick(returnRequests[0])}
            >
              <div className="flex gap-4">
                {returnRequests[0].bookCoverImage && (
                  <img
                    src={
                      returnRequests[0].bookCoverImage && returnRequests[0].bookCoverImage !== "defaultImage.jpg"
                        ? `${config.url}${returnRequests[0].bookCoverImage}`
                        : "/images/defaultBook.png"
                    }
                    alt={returnRequests[0].bookName}
                    className="w-20 h-28 object-cover rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.src = "/images/defaultBook.png";
                    }}
                  />
                )}
                <div className="flex-1 min-w-0">
                  <div className="flex items-start justify-between mb-2">
                    <div className="flex-1">
                      <h4 className="font-bold text-gray-900 text-base line-clamp-1">
                        {returnRequests[0].bookName}
                      </h4>
                      <p className="text-sm text-gray-600">{returnRequests[0].bookAuthor}</p>
                    </div>
                    <span
                      className={`ml-2 px-3 py-1 rounded-full text-xs font-semibold whitespace-nowrap ${
                        returnRequests[0].deliveryStatus === "RETURN_REQUESTED"
                          ? "bg-orange-100 text-orange-800"
                          : returnRequests[0].deliveryStatus === "PICKUP_SCHEDULED"
                          ? "bg-indigo-100 text-indigo-800"
                          : "bg-pink-100 text-pink-800"
                      }`}
                    >
                      {returnRequests[0].deliveryStatus.replace(/_/g, " ")}
                    </span>
                  </div>
                  
                  <div className="space-y-2 mt-3">
                    <div className="flex items-center text-sm text-gray-700">
                      <div className="w-6 h-6 bg-purple-100 rounded-full flex items-center justify-center mr-2">
                        <span className="text-purple-600 font-semibold text-xs">
                          {returnRequests[0].customerName?.charAt(0) || "?"}
                        </span>
                      </div>
                      <span className="font-medium">{returnRequests[0].customerName || "N/A"}</span>
                    </div>
                    
                    <div className="flex items-start text-sm text-gray-600">
                      <Package size={14} className="mr-2 mt-0.5 text-purple-500 flex-shrink-0" />
                      <span className="line-clamp-1">{returnRequests[0].deliveryAddress}</span>
                    </div>
                    
                    <div className="flex items-center text-sm font-medium text-purple-600">
                      <span className="mr-1">{returnRequests[0].city}</span>
                      <span>•</span>
                      <span className="ml-1">{returnRequests[0].pincode}</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>


      {/* Quick Actions Grid */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 sm:gap-6">
        {/* Today's Deliveries */}
        <div className="bg-gradient-to-br from-primary via-orange-500 to-orange-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white relative overflow-hidden transform hover:scale-105 transition-transform duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full -ml-12 -mb-12"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold opacity-95">Today's Deliveries</h3>
              <div className="p-3 bg-white/20 rounded-xl">
                <CheckCircle size={24} className="opacity-90" />
              </div>
            </div>
            <p className="text-5xl sm:text-6xl font-extrabold mb-3">{stats.completedToday || 0}</p>
            <p className="text-sm opacity-95 font-medium">
              {stats.completedToday > 0 ? "Great work! 🎉" : "Let's get started! 💪"}
            </p>
          </div>
        </div>

        {/* Pending Deliveries */}
        <div className="bg-gradient-to-br from-blue-500 via-blue-600 to-indigo-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white relative overflow-hidden transform hover:scale-105 transition-transform duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full -ml-12 -mb-12"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold opacity-95">Active Orders</h3>
              <div className="p-3 bg-white/20 rounded-xl">
                <Truck size={24} className="opacity-90" />
              </div>
            </div>
            <p className="text-5xl sm:text-6xl font-extrabold mb-4">{stats.pendingDeliveries || 0}</p>
            <button
              onClick={() => navigate("/delivery/orders?status=ALL")}
              className="text-sm bg-white text-blue-600 px-6 py-3 rounded-xl hover:bg-blue-50 transition font-bold shadow-lg hover:shadow-xl w-full sm:w-auto"
            >
              View Orders
            </button>
          </div>
        </div>

        {/* Return Requests */}
        <div className="bg-gradient-to-br from-purple-500 via-purple-600 to-pink-600 rounded-2xl shadow-xl p-6 sm:p-8 text-white relative overflow-hidden transform hover:scale-105 transition-transform duration-300">
          <div className="absolute top-0 right-0 w-32 h-32 bg-white opacity-10 rounded-full -mr-16 -mt-16"></div>
          <div className="absolute bottom-0 left-0 w-24 h-24 bg-white opacity-5 rounded-full -ml-12 -mb-12"></div>
          <div className="relative">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-base font-bold opacity-95">Return Requests</h3>
              <div className="p-3 bg-white/20 rounded-xl">
                <Package size={24} className="opacity-90" />
              </div>
            </div>
            <p className="text-5xl sm:text-6xl font-extrabold mb-4">
              {(stats.returnRequestedOrders || 0) + (stats.pickupScheduledOrders || 0) + (stats.returnInTransitOrders || 0)}
            </p>
            <button
              onClick={() => navigate("/delivery/returns")}
              className="text-sm bg-white text-purple-600 px-6 py-3 rounded-xl hover:bg-purple-50 transition font-bold shadow-lg hover:shadow-xl w-full sm:w-auto"
            >
              View Returns
            </button>
          </div>
        </div>
      </div>

     
      {/* Recent Orders & Returns - Side by Side */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent Delivery Orders */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-blue-50 to-indigo-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Orders</h2>
                <p className="text-sm text-gray-600 mt-1">Latest delivery assignments</p>
              </div>
              <button
                onClick={() => navigate("/delivery/orders")}
                className="flex items-center space-x-2 px-4 py-2 bg-blue-500 text-white rounded-xl hover:bg-blue-600 transition font-semibold shadow-md hover:shadow-lg"
              >
                <span>View All</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {orders.loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500 mx-auto"></div>
                <p className="text-gray-500 mt-3">Loading orders...</p>
              </div>
            ) : deliveryOrders.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Truck size={32} className="text-blue-400" />
                </div>
                <p className="text-gray-600 font-medium">No orders found</p>
                <p className="text-sm text-gray-500 mt-1">Orders will appear here when assigned</p>
            </div>
          ) : (
            deliveryOrders.slice(0, 5).map((order) => (
              <div
                key={order.orderId}
                className="p-4 hover:bg-gray-50 transition cursor-pointer group"
                onClick={() => handleOrderClick(order)}
              >
                <div className="flex items-center justify-between gap-4">
                  <div className="flex items-center space-x-3 flex-1 min-w-0">
                    {order.bookCoverImage && (
                      <img
                        src={
                          order.bookCoverImage && order.bookCoverImage !== "defaultImage.jpg"
                            ? `${config.url}${order.bookCoverImage}`
                            : "/images/defaultBook.png"
                        }
                        alt={order.bookName}
                        className="w-12 h-16 object-cover rounded shadow-sm"
                        onError={(e) => {
                          e.target.src = "/images/defaultBook.png";
                        }}
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-gray-900 truncate group-hover:text-primary transition">
                        {order.bookName}
                      </h3>
                      <p className="text-sm text-gray-600 truncate">
                        {order.customerName}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5">
                        {order.city} - {order.pincode}
                      </p>
                    </div>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <span
                      className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                        order.deliveryStatus === "PROCESSING"
                          ? "bg-blue-100 text-blue-800"
                          : order.deliveryStatus === "SHIPPED"
                          ? "bg-purple-100 text-purple-800"
                          : order.deliveryStatus === "ON_THE_WAY"
                          ? "bg-yellow-100 text-yellow-800"
                          : "bg-green-100 text-green-800"
                      }`}
                    >
                      {order.deliveryStatus.replace(/_/g, " ")}
                    </span>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
        </div>

        {/* Recent Return Requests */}
        <div className="bg-white rounded-2xl shadow-lg border border-gray-100">
          <div className="p-6 border-b border-gray-100 bg-gradient-to-r from-purple-50 to-pink-50">
            <div className="flex items-center justify-between">
              <div>
                <h2 className="text-xl font-bold text-gray-900">Recent Returns</h2>
                <p className="text-sm text-gray-600 mt-1">Latest return requests</p>
              </div>
              <button
                onClick={() => navigate("/delivery/returns")}
                className="flex items-center space-x-2 px-4 py-2 bg-purple-500 text-white rounded-xl hover:bg-purple-600 transition font-semibold shadow-md hover:shadow-lg"
              >
                <span>View All</span>
                <ArrowRight size={18} />
              </button>
            </div>
          </div>

          <div className="divide-y divide-gray-100">
            {orders.loading ? (
              <div className="p-8 text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-500 mx-auto"></div>
                <p className="text-gray-500 mt-3">Loading returns...</p>
              </div>
            ) : returnRequests.length === 0 ? (
              <div className="p-8 text-center">
                <div className="w-16 h-16 bg-purple-100 rounded-full flex items-center justify-center mx-auto mb-3">
                  <Package size={32} className="text-purple-400" />
                </div>
                <p className="text-gray-600 font-medium">No return requests</p>
                <p className="text-sm text-gray-500 mt-1">Return requests will appear here</p>
              </div>
            ) : (
              returnRequests.slice(0, 5).map((order) => (
                <div
                  key={order.orderId}
                  className="p-4 hover:bg-purple-50 transition cursor-pointer group"
                  onClick={() => handleOrderClick(order)}
                >
                  <div className="flex items-center justify-between gap-4">
                    <div className="flex items-center space-x-3 flex-1 min-w-0">
                      {order.bookCoverImage && (
                        <img
                          src={
                            order.bookCoverImage && order.bookCoverImage !== "defaultImage.jpg"
                              ? `${config.url}${order.bookCoverImage}`
                              : "/images/defaultBook.png"
                          }
                          alt={order.bookName}
                          className="w-12 h-16 object-cover rounded shadow-sm"
                          onError={(e) => {
                            e.target.src = "/images/defaultBook.png";
                          }}
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <h3 className="font-semibold text-gray-900 truncate group-hover:text-purple-600 transition">
                          {order.bookName}
                        </h3>
                        <p className="text-sm text-gray-600 truncate">
                          {order.customerName}
                        </p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          {order.city} - {order.pincode}
                        </p>
                      </div>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <span
                        className={`inline-block px-3 py-1 rounded-full text-xs font-semibold ${
                          order.deliveryStatus === "RETURN_REQUESTED"
                            ? "bg-orange-100 text-orange-800"
                            : order.deliveryStatus === "PICKUP_SCHEDULED"
                            ? "bg-indigo-100 text-indigo-800"
                            : "bg-pink-100 text-pink-800"
                        }`}
                      >
                        {order.deliveryStatus.replace(/_/g, " ")}
                      </span>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      </div>

      {/* Order Details Modal */}
      {selectedOrder && (
        <DeliveryOrderDetails
          order={selectedOrder}
          onClose={handleCloseDetails}
          onUpdateStatus={handleUpdateStatus}
          onVerifyOtp={handleVerifyOtp}
        />
      )}
    </div>
  );
};

export default DeliveryDashboard;
